import { defineStore } from 'pinia'
import registrationAPI from '@vue3/api/registration-api'
import { emitEventToVuexStore } from '@vue3/utils/common.js'
import AuthStore from '@vue3/store/auth-store'

const registrationStore = defineStore('registrationStore', {
  state: () => ({
    showContinueButtonLoader: false,
    showCancelButtonLoader: false,
    isFileUploading: false,
    isUserAlreadyVisitedReason: false,
    availableTermsOfPayment: [],
    dealerDepoList: [],
    dealerCategoryList: [],
    registrationType: '',
    dealerData: {},
    picData: {},
    supportDocument: {},
    creditLimit: {},
    provinceList: [],
    registrationNextStep: '',
    defaultPaymentMethod: '',
    incompleteRegistrationDate: null,
    companyId: null,
    selectedTenants: []
  }),
  actions: {
    setFileUploading (value) {
      this.isFileUploading = value
    },
    setRegistrationStep (value) {
      this.registrationNextStep = value
      AuthStore().updateRegistrationStep(this.registrationNextStep)
      emitEventToVuexStore('PINIA_UPDATE_REGISTRATION_STEP', this.registrationNextStep)
    },
    setDefaultPaymentMethod (value) {
      this.defaultPaymentMethod = value
    },
    setIsUserAlreadyVisitedReason (value) {
      this.isUserAlreadyVisitedReason = value
    },
    setSelectedTenants (value) {
      this.selectedTenants = value
    },
    togglContinueButtonLoader () {
      this.showContinueButtonLoader = !this.showContinueButtonLoader
    },
    toggelCancelButtonLoader () {
      this.showCancelButtonLoader = !this.showCancelButtonLoader
    },
    updateUserStateValue (data) {
      this.registrationType = data?.registrationType || ''
      this.registrationDetails = data
      this.selectedTenants = data?.chosenTenantIds || []
      this.dealerData = getMappedDealerData(data)
      this.picData = getMappedPicData(data)
      this.companyId = data.companyId
      this.supportDocument = data?.supportingDocResponse || {}
      this.creditLimit = getMappedCreditLimitData(data, this.defaultPaymentMethod)
      this.setRegistrationStep(data?.companyDetails?.registrationNextStep || data?.registrationNextStep || '')
      this.incompleteRegistrationDate = data?.incompleteRegistrationResponse?.nextRequestDate || null
    },
    resetRegistrationStoreData () {
      this.registrationType = ''
      this.dealerData = {}
      this.picData = {}
      this.supportDocument = {}
      this.creditLimit = {}
    },
    getUserRegistrationDetails ({ query, success, fail } = {}) {
      registrationAPI.getUserRegistrationDetails(query).then(res => {
        const resBody = res?.body || {}
        if (resBody.success) {
          const data = resBody?.value || {}
          this.updateUserStateValue(data)
          success && success(resBody)
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    getAvailableTermsOfPayment ({ success = () => { }, fail = () => { } } = {}) {
      registrationAPI.getAvailableTermsOfPayment().then(
        res => {
          const resBody = res.body
          if (resBody && resBody.success) {
            this.availableTermsOfPayment = resBody?.value?.availableTermsOfPayments || []
            success(this.availableTermsOfPayment)
          } else {
            this.availableTermsOfPayment = []
            fail(res)
          }
        },
        err => {
          fail(err)
        }
      )
    },
    updateRegistrationType ({ data, success, fail } = {}) {
      registrationAPI.updateRegistrationType(data).then(res => {
        const resBody = res?.body
        if (resBody?.success) {
          success && success(resBody)
        } else {
          fail && fail(resBody)
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    postRegisterData ({ data, success, fail } = {}) {
      registrationAPI.postRegisterData(data).then(res => {
        const resBody = res?.body || {}
        if (resBody.success) {
          success && success(resBody)
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    fileUpload ({ data, success, fail } = {}) {
      registrationAPI.uploadFile(data).then(res => {
        const resBody = res.body || {}
        if (resBody?.success) {
          success && success(resBody)
        } else {
          fail && fail(resBody)
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    getCategoryAndDepo ({ success, fail } = {}) {
      registrationAPI.getCategoryAndDepo().then(res => {
        const resValue = res?.body?.value || {}
        this.dealerDepoList = resValue?.dealerDepoData || []
        this.dealerCategoryList = resValue?.dealerCategoryData || []
        success && success()
      }).catch(err => {
        fail && fail(err)
      })
    },
    getAllProvince ({ success, fail } = {}) {
      this.provinceList = []
      registrationAPI.getAllProvince().then(res => {
        const resBody = res?.body || {}
        const content = resBody?.content || []
        if (resBody.success && content.length) {
          this.provinceList = content
          success && success()
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    getCitiesByProvince ({ data, success, fail } = {}) {
      registrationAPI.getCitiesByProvince(data).then(res => {
        const resBody = res?.body || {}
        const content = resBody?.content || []
        if (resBody.success && content.length) {
          success && success(content)
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    getDistrictByCityCode ({ data, success, fail } = {}) {
      registrationAPI.getDistrictByCityCode(data).then(res => {
        const resBody = res?.body || {}
        const content = resBody?.content || []
        if (resBody.success && content.length) {
          success && success(content)
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    getSubDistrictByDistrictCode ({ data, success, fail } = {}) {
      registrationAPI.getSubDistrictByDistrictCode(data).then(res => {
        const resBody = res?.body || {}
        const content = resBody?.content || []
        if (resBody.success && content.length) {
          success && success(content)
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    getPostalCodeBySubDistrictCode ({ data, success, fail } = {}) {
      registrationAPI.getPostalCodeBySubDistrictCode(data).then(res => {
        const resBody = res?.body || {}
        const content = resBody?.content || []
        if (resBody.success && content.length) {
          const postalCodeList = content?.map(item => {
            return {
              code: item.postalCode,
              name: item.postalCode
            }
          }) || []
          success && success(postalCodeList)
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    markUserLogout ({ success, fail } = {}) {
      registrationAPI.markUserLogout().then(res => {
        const resBody = res?.body || {}
        if (resBody.success) {
          success && success()
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    firstLoginPostApproval ({ success, fail } = {}) {
      registrationAPI.firstLoginPostApproval().then(res => {
        const resBody = res?.body || {}
        if (resBody.success) {
          success && success()
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    completeRegistrationProcess ({ data, success, fail } = {}) {
      registrationAPI.completeRegistrationProcess(data).then(res => {
        success && success(res.body)
      }).catch(err => {
        fail && fail(err)
      })
    },
    getAvailableTenants ({ success, fail } = {}) {
      registrationAPI.getAvailableTenants().then(res => {
        const resBody = res?.body || {}
        const content = resBody?.content || []
        if (resBody.success && content.length) {
          success && success(content)
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    getTenantDistributorInfo ({ success, fail } = {}) {
      registrationAPI.getTenantDistributorInfo().then(res => {
        const resBody = res?.body || {}
        const content = resBody?.content || []
        if (resBody.success && content.length) {
          success && success(content)
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    getAccountRegistrationSummary ({ success, fail } = {}) {
      registrationAPI.getAccountRegistrationSummary().then(res => {
        const resBody = res?.body || {}
        if (resBody.success) {
          success && success(resBody)
        } else {
          fail && fail()
        }
      }).catch(err => {
        fail && fail(err)
      })
    }
  }
}
)

const getMappedDealerData = (data) => {
  const { companyName, companyEmail, companyPhoneNumber, buildingStatus, yearsOfOldBuilding, addressResponse } = data?.companyInformationResponse || {}
  const { shippingAddressResponse, additionalInformationResponse, legalDocumentResponse } = data || {}
  const shippingAddressList = shippingAddressResponse?.map(({ defaultAddress = false, addressLabel = '', addressResponse = {} }) => ({
    defaultAddress,
    addressLabel,
    addressRequest: {
      address: addressResponse?.address || '',
      province: {
        code: addressResponse?.province?.code || '',
        name: addressResponse?.province?.name || ''
      },
      town: {
        code: addressResponse?.town?.code || '',
        name: addressResponse?.town?.name || ''
      },
      district: {
        code: addressResponse?.district?.code || '',
        name: addressResponse?.district?.name || ''
      },
      subDistrict: {
        code: addressResponse?.subDistrict?.code || '',
        name: addressResponse?.subDistrict?.name || ''
      },
      postalCode: addressResponse?.postalCode || ''
    }
  })) || []

  const { dealerDepo, dealerCategory, blibliSellerId } = additionalInformationResponse || {}
  const { sppkpImageUrl, npwpNumber, npwpAddress, npwpImageUrl,
    nibImageUrl, skDomisiliImageUrl, aktaPendirianImageUrl,
    skPendirianMenteriImageUrl, aktaPerubahanImageUrl,
    skPerubahanMenteriImageUrl } = legalDocumentResponse || {}
  return {
    contactDetails: {
      name: companyName || '',
      email: companyEmail || '',
      phoneNumber: companyPhoneNumber || ''
    },
    addressDetails: {
      fullAddress: addressResponse?.address || '',
      province: {
        code: addressResponse?.province?.code || '',
        name: addressResponse?.province?.name || ''
      },
      town: {
        code: addressResponse?.town?.code || '',
        name: addressResponse?.town?.name || ''
      },
      district: {
        code: addressResponse?.district?.code || '',
        name: addressResponse?.district?.name || ''
      },
      subDistrict: {
        code: addressResponse?.subDistrict?.code || '',
        name: addressResponse?.subDistrict?.name || ''
      },
      postalCode: addressResponse?.postalCode || '',
      durationOfStay: yearsOfOldBuilding?.toString() || ''
    },
    additonalInformation: {
      dealerDepo: dealerDepo ? [dealerDepo] : [],
      dealerCategory: dealerCategory ? [dealerCategory] : [],
      blibliSellerId: blibliSellerId || ''
    },
    buildingStatus: buildingStatus || '',
    shippingAddressList,
    legalDocument: {
      npwpNumber: npwpNumber || '',
      npwpAddress: npwpAddress || '',
      sppkpImageUrl: sppkpImageUrl || [],
      npwpImageUrl: npwpImageUrl || [],
      nibImageUrl: nibImageUrl || [],
      skDomisiliImageUrl: skDomisiliImageUrl || [],
      aktaPendirianImageUrl: aktaPendirianImageUrl || [],
      skPendirianMenteriImageUrl: skPendirianMenteriImageUrl || [],
      aktaPerubahanImageUrl: aktaPerubahanImageUrl || [],
      skPerubahanMenteriImageUrl: skPerubahanMenteriImageUrl || []
    }
  }
}

const getMappedPicData = (data) => {
  const {
    directorName,
    directorEmail,
    directorNumber,
    ktpNumber,
    ktpImageUrl,
    npwpNumber,
    npwpImageUrl,
    npwpAddress
  } = data?.directorInfoResponse || {}

  return {
    ktpNumber: ktpNumber || '',
    npwpNumber: npwpNumber || '',
    npwpAddress: npwpAddress || '',
    ktpImageUrl: ktpImageUrl || [],
    npwpImageUrl: npwpImageUrl || [],
    directorDetails: {
      name: directorName || '',
      email: directorEmail || '',
      phoneNumber: directorNumber || ''
    },
    picList: data?.companyPicResponses || []
  }

}

const getMappedCreditLimitData = (data, defaultPaymentMethod) => {
  const { creditLimitAmount, creditLimitPayment } = data?.creditLimitResponse || {}
  return {
    creditLimit: creditLimitAmount || '',
    paymentMethod: creditLimitPayment ? [creditLimitPayment] : [defaultPaymentMethod]
  }
}

export default registrationStore
